import React, { useEffect, useRef } from 'react';

const CLAY_FONT = 'var(--clay-font)';

interface LeaveLessonDialogProps {
  onStay: () => void;
  onLeave: () => void;
}

/**
 * LeaveLessonDialog — the "Leave the lesson?" confirmation from the Claude
 * Design lesson-intro redesign. A calm overlay with a cream clay card, a red
 * warning bead, and two clay pills: "Stay here" (green, cancels) and "Leave"
 * (red, confirms exit). Shown when the player's close control is pressed;
 * confirming runs the player's real exit. Escape / backdrop click = stay.
 */
export const LeaveLessonDialog = ({
  onStay,
  onLeave,
}: LeaveLessonDialogProps) => {
  const stayRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    stayRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onStay();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onStay]);

  return (
    <div
      className="theme-app absolute inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(31,44,25,0.5)' }}
      onClick={onStay}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="leave-lesson-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(560px, 86%)',
          background: '#FFFDF4',
          borderRadius: 32,
          padding: '44px 44px 38px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.32)',
          textAlign: 'center',
          fontFamily: CLAY_FONT,
        }}
      >
        <div
          aria-hidden
          style={{
            width: 88,
            height: 88,
            margin: '0 auto 24px',
            borderRadius: '50%',
            background:
              'radial-gradient(circle at 38% 30%, #F0796D 0%, #DA3B2E 62%, #BE2C21 100%)',
            boxShadow:
              'inset 0 6px 9px rgba(255,255,255,0.4), inset 0 -9px 12px rgba(0,0,0,0.28), 0 8px 14px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 56,
            fontWeight: 700,
            color: '#fff',
            lineHeight: 1,
          }}
        >
          !
        </div>
        <h2
          id="leave-lesson-title"
          style={{
            margin: '0 0 14px',
            fontSize: 34,
            fontWeight: 800,
            color: '#1F4A2E',
          }}
        >
          Leave the lesson?
        </h2>
        <p
          style={{
            margin: '0 0 32px',
            fontSize: 23,
            lineHeight: 1.42,
            fontWeight: 700,
            color: '#4A5A44',
          }}
        >
          You can come back and start this lesson any time you like.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 20,
            flexWrap: 'wrap',
          }}
        >
          <button
            ref={stayRef}
            type="button"
            onClick={onStay}
            className="clay-press"
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '20px 36px',
              borderRadius: 999,
              fontFamily: 'inherit',
              fontWeight: 800,
              fontSize: 24,
              color: '#fff',
              background:
                'radial-gradient(circle at 42% 22%, #4FB06F 0%, #2E8B4F 62%, #217A41 100%)',
              boxShadow:
                'inset 0 7px 10px rgba(255,255,255,0.42), inset 0 -9px 13px rgba(0,0,0,0.28), 0 8px 15px rgba(0,0,0,0.2)',
            }}
          >
            Stay here
          </button>
          <button
            type="button"
            onClick={onLeave}
            className="clay-press"
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '20px 36px',
              borderRadius: 999,
              fontFamily: 'inherit',
              fontWeight: 800,
              fontSize: 24,
              color: '#fff',
              background:
                'radial-gradient(circle at 42% 22%, #F0796D 0%, #DA3B2E 62%, #BE2C21 100%)',
              boxShadow:
                'inset 0 7px 10px rgba(255,255,255,0.42), inset 0 -9px 13px rgba(0,0,0,0.28), 0 8px 15px rgba(0,0,0,0.2)',
            }}
          >
            Leave
          </button>
        </div>
      </div>
    </div>
  );
};
